import { BinRefs, Bins, StepDistribution } from './types'
import { Assignation, UNASSIGNED } from '../../../context/types'
import { AssignedBuckets } from '../../../services/types'
import {
    CATS_TYPE,
    DIMENSIONS,
    DIMENSIONS_TYPE,
    VALUES_3,
} from '../../../utils/values/cats'
import { AppState } from './types'
import { doShuffle } from '../../../context/helpers/arrays'
import { computePages } from '../hooks/usePaginatedBin'
import { initialBinStore, maxBucketCapacities } from './constants'
import { debugStm, debugStmsDeep } from './utils'

type TValidateBinState = {
    isToAssign: boolean
    exceedingBinRefs: BinRefs[]
    insufficientBinRefs: BinRefs[]
    selectedIsExceeding: boolean
    selectedIsInsufficient: boolean
    selectedIsEmpty: boolean
    allAssignedExceeding: boolean
    sortingIsDone: boolean
}

export const validateBinState = (
    bins: Bins,
    selectedBinRef: BinRefs | null
): TValidateBinState => {
    const isToAssign = selectedBinRef === 'to_assign'

    const exceedingBinRefs = Object.values(bins)
        .filter((bin) => bin.stms.length > bin.max)
        .map((bin) => bin.ref) as BinRefs[]

    const insufficientBinRefs = Object.values(bins)
        .filter((bin) => bin.stms.length < bin.max)
        .map((bin) => bin.ref) as BinRefs[]

    const selectedIsExceeding =
        !isToAssign &&
        selectedBinRef !== null &&
        exceedingBinRefs.includes(selectedBinRef)

    const selectedIsInsufficient =
        !isToAssign &&
        selectedBinRef !== null &&
        insufficientBinRefs.includes(selectedBinRef)

    const selectedIsEmpty =
        !isToAssign &&
        selectedBinRef !== null &&
        bins[selectedBinRef].stms.length === 0

    const allAssignedExceeding =
        bins.to_assign.stms.length === 0 && exceedingBinRefs.length > 0

    const sortingIsDone =
        bins.to_assign.stms.length === 0 && exceedingBinRefs.length === 0

    return {
        isToAssign,
        exceedingBinRefs,
        insufficientBinRefs,
        selectedIsExceeding,
        selectedIsInsufficient,
        selectedIsEmpty,
        allAssignedExceeding,
        sortingIsDone,
    }
}


const setDiversityScore = (stm: Assignation, visibleStms: Assignation[]) => {
    // Count current distributions
    // console.debug('==== setDiversityScore', debugStm(stm))
    const categoryCounts = new Map<CATS_TYPE, number>()
    const dimensionKeyCounts = new Map<DIMENSIONS_TYPE, number>()
    visibleStms.forEach((stm) => {
        if (stm.category && stm.dimensionKey) {
            categoryCounts.set(
                stm.category as CATS_TYPE,
                (categoryCounts.get(stm.category as CATS_TYPE) || 0) + 1
            )
            dimensionKeyCounts.set(
                stm.dimensionKey as DIMENSIONS_TYPE,
                (dimensionKeyCounts.get(stm.dimensionKey as DIMENSIONS_TYPE) ||
                    0) + 1
            )
        }
    })
    // console.debug('>>> setDiversityScore, categoryCounts', categoryCounts)
    // console.debug('>>> setDiversityScore, dimensionKeyCounts', dimensionKeyCounts)
    const categoryScore = stm.category
        ? categoryCounts.get(stm.category) ?? 0
        : 0
    const dimensionKeyScore = stm.dimensionKey
        ? dimensionKeyCounts.get(stm.dimensionKey) ?? 0
        : 0
    // if we have equal categoryScore, then we will dimensionKeyScore
    stm.diversityScore = categoryScore + dimensionKeyScore
}

export const setDiversityScores = (
    stms: Assignation[],
    visibleStms: Assignation[]
) => {
    stms.forEach((stm) => setDiversityScore(stm, visibleStms))
}

export const pickReplacement = (
    newlyRankedStm: Assignation,
    visibleStmsToRank: Assignation[],
    invisibleStmsToRank: Assignation[]
): Assignation | undefined => {
    // Goal : replace the newly ranked stm with one invisible stm, which was removed from the visibleStmsToRank
    // Constraint : the visibleStmsToRank should remain as diverse as possible
    // - Axe 1 of diversity: the distribution of valueKey should as different as possible
    // - Axe 2 of diversity: the dimensionKey should as different as possible
    // - Axe 3 of diversity ?: if non above are possible, replace by a random stm of the same valueKey as the newly ranked stm
    // - Axe 4 : If non above are possible, then use a random one
    const availableStms = invisibleStmsToRank.slice()

    // Score each invisible statement based on diversity
    // TODO binstore immutable method?
    setDiversityScores(availableStms, visibleStmsToRank)
    // console.debug('>>> pickReplacement, setDiversityScores', debugStmsDeep({visibleStmsToRank}), debugStmsDeep({availableStms}))

    // Sort by score in ascending order
    availableStms.sort((a, b) => a.diversityScore! - b.diversityScore!)
    console.debug(
        '>>> pickReplacement, sorted diversityScore',
        debugStmsDeep({ availableStms })
    )
    // If we have a strict winner, use it
    if (
        availableStms.length > 1 &&
        availableStms[0].diversityScore! > availableStms[1].diversityScore!
    ) {
        return availableStms[0]
    } else {
        // Pick a random one among the co-winner ones
        const coWinners = availableStms.filter(
            (s) => s.diversityScore === availableStms[0].diversityScore
        )
        console.debug(
            '>>> pickReplacement, coWinners',
            debugStmsDeep({ coWinners })
        )
        return coWinners?.[Math.floor(Math.random() * coWinners.length)]
    }
}

const reorderByDim = (toRank: Assignation[]) => {
    const dimensions: DIMENSIONS_TYPE[] = doShuffle(Object.values(DIMENSIONS))
    const ordered: Assignation[] = []
    // FIXME Map<K,V>
    // console.debug('reorderByDim', 'toRank:', toRank)
    const splitByDim: Record<DIMENSIONS_TYPE, Assignation[]> = toRank.reduce(
        (acc, curr) => {
            const dim = curr.dimensionKey as DIMENSIONS_TYPE
            const prev = acc?.[dim] ?? []
            acc[dim] = [...prev, curr]
            return acc
        },
        {} as any
    )
    // console.debug('reorderByDim', 'splitByDim:', splitByDim)
    while (ordered.length !== toRank.length) {
        for (let dim of dimensions) {
            const a: Assignation | undefined = splitByDim?.[dim]?.pop()
            a && ordered.push(a)
        }
    }
    // console.debug(
    //     'reorderByDim =>',
    //     ordered.map((a) => a.dimensionKey)
    // )
    return ordered
}

export const initBins = (
    toRank: Assignation[],
    stepDistribution: StepDistribution,
    maxPerPage: number
): Bins => {
    const toAssignStms = reorderByDim(toRank.slice()) // 1st diversity trick
    const pageInfo = computePages(
        toAssignStms,
        initialBinStore.page,
        maxPerPage
    )
    setDiversityScores(toAssignStms, pageInfo.stmsForPage) // Assess diversity
    return {
        to_assign: {
            stms: toAssignStms,
            max: toRank.length,
            ref: 'to_assign',
        },
        least_important: {
            stms: [],
            max: stepDistribution[0],
            ref: 'least_important',
        },
        moderately_important: {
            stms: [],
            max: stepDistribution[1],
            ref: 'moderately_important',
        },
        most_important: {
            stms: [],
            max: stepDistribution[2],
            ref: 'most_important',
        },
    }
}

// FIXME VALUES_3 vs VALUES
const defaultBuckets: Pick<AssignedBuckets, VALUES_3> = {
    samen1: UNASSIGNED,
    conf1: UNASSIGNED,
    erken1: UNASSIGNED,
    zorg1: UNASSIGNED,
    gelijk1: UNASSIGNED,
    zelfsturing1: UNASSIGNED,
    macht1: UNASSIGNED,
    voorspelbaar1: UNASSIGNED,
    prestatie1: UNASSIGNED,
    zelfontplooi1: UNASSIGNED,
    duurzaam1: UNASSIGNED,
    integriteit1: UNASSIGNED,
    innovatie1: UNASSIGNED,
    daadkracht1: UNASSIGNED,
    samen2: UNASSIGNED,
    conf2: UNASSIGNED,
    erken2: UNASSIGNED,
    zorg2: UNASSIGNED,
    gelijk2: UNASSIGNED,
    zelfsturing2: UNASSIGNED,
    macht2: UNASSIGNED,
    voorspelbaar2: UNASSIGNED,
    prestatie2: UNASSIGNED,
    zelfontplooi2: UNASSIGNED,
    duurzaam2: UNASSIGNED,
    integriteit2: UNASSIGNED,
    innovatie2: UNASSIGNED,
    daadkracht2: UNASSIGNED,
    samen3: UNASSIGNED,
    conf3: UNASSIGNED,
    erken3: UNASSIGNED,
    zorg3: UNASSIGNED,
    gelijk3: UNASSIGNED,
    zelfsturing3: UNASSIGNED,
    macht3: UNASSIGNED,
    voorspelbaar3: UNASSIGNED,
    prestatie3: UNASSIGNED,
    zelfontplooi3: UNASSIGNED,
    duurzaam3: UNASSIGNED,
    integriteit3: UNASSIGNED,
    innovatie3: UNASSIGNED,
    daadkracht3: UNASSIGNED,
}

export const buckets2assignment = (buckets: Assignation[][]) => {
    const output = defaultBuckets
    buckets[0]?.forEach((stm) => (output[stm.valueKey as VALUES_3] = 1))
    buckets[1]?.forEach((stm) => (output[stm.valueKey as VALUES_3] = 2))
    buckets[2]?.forEach((stm) => (output[stm.valueKey as VALUES_3] = 3))
    buckets[3]?.forEach((stm) => (output[stm.valueKey as VALUES_3] = 4))
    buckets[4]?.forEach((stm) => (output[stm.valueKey as VALUES_3] = 5))
    buckets[5]?.forEach((stm) => (output[stm.valueKey as VALUES_3] = 6))
    buckets[6]?.forEach((stm) => (output[stm.valueKey as VALUES_3] = 7))
    buckets[7]?.forEach((stm) => (output[stm.valueKey as VALUES_3] = 8))
    buckets[8]?.forEach((stm) => (output[stm.valueKey as VALUES_3] = 9))
    return output as AssignedBuckets
}

// FIXME refactor simplify
export const rankedStms2assignment = (
    rankedStms: AppState['rankedStms'],
    nBucket: 7 | 9
): AssignedBuckets => {
    const buckets = rankedStms2buckets(rankedStms, nBucket)
    const output = buckets2assignment(buckets)
    return output as AssignedBuckets //FIXME: fix this cast
}

export const rankedStms2buckets = (
    rankedStms: AppState['rankedStms'],
    nBucket: 7 | 9
): Assignation[][] => {
    const b1: Assignation[] = rankedStms[1]?.least_important.stms ?? []
    const b2: Assignation[] = rankedStms[1]?.moderately_important.stms ?? []
    const b3: Assignation[] = rankedStms[1]?.most_important.stms ?? []
    const b7: Assignation[] = rankedStms[2]?.least_important.stms ?? []
    const b8: Assignation[] = rankedStms[2]?.moderately_important.stms ?? []
    const b9: Assignation[] = rankedStms[2]?.most_important.stms ?? []
    const b4: Assignation[] = []
    const b6: Assignation[] = []
    const b5: Assignation[] = []
    if (nBucket === 7) {
        b5.push(...(rankedStms[0]?.moderately_important.stms ?? []))
    } else if (nBucket === 9) {
        b4.push(...(rankedStms[3]?.least_important.stms ?? []))
        b5.push(...(rankedStms[3]?.moderately_important.stms ?? []))
        b6.push(...(rankedStms[3]?.most_important.stms ?? []))
    }
    return [b1, b2, b3, b4, b5, b6, b7, b8, b9]
}

export const getOverfilledBuckets = (buckets: Assignation[][], nBucket: 7 | 9) => {
    return buckets
        .map((bucket, index) => ({
            bucketIndex: index,
            itemCount: bucket.length,
            maxCapacity: maxBucketCapacities[nBucket][index],
        }))
        .filter(({ itemCount, maxCapacity }) => itemCount > maxCapacity)
        .map(({ bucketIndex }) => bucketIndex)
}
export const getUnderfilledBuckets = (buckets: Assignation[][], nBucket: 7 | 9) => {
    return buckets
        .map((bucket, index) => ({
            bucketIndex: index,
            itemCount: bucket.length,
            maxCapacity: maxBucketCapacities[nBucket][index],
        }))
        .filter(({ itemCount, maxCapacity }) => itemCount > maxCapacity)
        .map(({ bucketIndex }) => bucketIndex)
}

export const getHighlightedBars = (
    status: AppState['status'],
    binRef: BinRefs
) => {
    switch (status) {
        case 'S1_42':
            switch (binRef) {
                case 'least_important':
                    return [0, 1, 2]
                case 'moderately_important':
                    return [3, 4, 5]
                case 'most_important':
                    return [6, 7, 8]
                default:
                    return []
            }
        case 'S2_11_LOW':
            switch (binRef) {
                case 'least_important':
                    return [0]
                case 'moderately_important':
                    return [1]
                case 'most_important':
                    return [2]
                default:
                    return []
            }
        case 'S3_11_HIGH':
        case 'S4_11_HIGH':
            switch (binRef) {
                case 'least_important':
                    return [6]
                case 'moderately_important':
                    return [7]
                case 'most_important':
                    return [8]
                default:
                    return []
            }
        case 'S3_20_MID':
        case 'S4_20_MID':
            switch (binRef) {
                case 'least_important':
                    return [3]
                case 'moderately_important':
                    return [4]
                case 'most_important':
                    return [5]
                default:
                    return []
            }
    }
}
